import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AnsulCommissioningFormData,
    AnsulInspectionItem,
    NitrogenCylinder,
    AnsulDetector,
    AnsulNozzle,
    ManualActuator,
    SavedAnsulCommissioning,
    CommissioningReportType
} from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { generateId, saveAnsulCommissioningItem, generateAnsulCommissioningNumber } from '../utils';

interface AnsulCommissioningFormProps {
    onSave: () => void;
    onPreview: (report: SavedAnsulCommissioning) => void;
    editReport?: SavedAnsulCommissioning | null;
}

// Detector color options
const DETECTOR_COLORS: AnsulDetector['color'][] = [
    '74-black',
    '100-white',
    '138-blue',
    '182-red',
    '232-green',
    '260-stainless'
];

// Checklist item keys
const CHECKLIST_KEYS = [
    'systemInPlace',
    'manualActuatorClear',
    'tampersIntact',
    'serviceTagPresent',
    'nozzlesClean',
    'nozzleCapsIntact',
    'pressureGaugeOk',
    'tankWeightOk',
    'pipingIntact',
    'interlockWorks',
    'filtersClean',
    'testFuseOk',
    'manualTestOk'
];

// Available commissioning report technology types
const COMMISSIONING_TYPES: { value: CommissioningReportType; labelKey: string; icon: string }[] = [
    { value: 'ansul_r102', labelKey: 'ansulCommissioning.types.ansul_r102', icon: 'local_fire_department' },
    { value: 'uv', labelKey: 'ansulCommissioning.types.uv', icon: 'light_mode' },
    { value: 'kcw_ww', labelKey: 'ansulCommissioning.types.kcw_ww', icon: 'water_drop' },
    { value: 'marvel', labelKey: 'ansulCommissioning.types.marvel', icon: 'air' },
    { value: 'cold_mist', labelKey: 'ansulCommissioning.types.cold_mist', icon: 'ac_unit' },
    { value: 'mobichef', labelKey: 'ansulCommissioning.types.mobichef', icon: 'restaurant' },
];

// Initial form data
const getInitialFormData = (): AnsulCommissioningFormData => ({
    restaurantName: '',
    restaurantAddress: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    systemType: 'Ansul R-102',
    extinguishingAgent: 'Ansulex',
    agentBatchNumber: '',
    serviceCycle: '6kk',
    commissioningDate: new Date().toISOString().split('T')[0],
    nextServiceDate: '',
    tankVolume: '',
    tankFilledDate: new Date().toISOString().split('T')[0],
    fluidExpiryDate: '',
    nitrogenCylinders: [],
    cylinderRenewalDate: '',
    manualActuators: [],
    gasShutoffType: 'none',
    gasShutoffNotes: '',
    detectors: [],
    detectorNotes: '',
    nozzles: [],
    nozzleNotes: '',
    additionalNotes: '',
    technicianName: '',
    technicianPhone: '',
    technicianCompany: 'Halton Oy'
});

// Generate initial checklist
const getInitialChecklist = (): AnsulInspectionItem[] => {
    return CHECKLIST_KEYS.map(key => ({
        id: generateId(),
        labelKey: key,
        status: '',
        notes: ''
    }));
};

const AnsulCommissioningForm: React.FC<AnsulCommissioningFormProps> = ({ onSave, onPreview, editReport }) => {
    const { t } = useTranslation();
    const { settings } = useSettings();

    const [formData, setFormData] = useState<AnsulCommissioningFormData>(
        editReport?.formData || getInitialFormData()
    );
    const [checklist, setChecklist] = useState<AnsulInspectionItem[]>(
        editReport?.checklist || getInitialChecklist()
    );
    const [activeSection, setActiveSection] = useState<'restaurant' | 'system' | 'components' | 'checklist'>('restaurant');

    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AnsulCommissioningFormData, string>>>({});

    const [selectedReportType, setSelectedReportType] = useState<CommissioningReportType>(
        editReport?.reportType || 'ansul_r102'
    );

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (fieldErrors[name as keyof AnsulCommissioningFormData]) {
            setFieldErrors(prev => {
                const next = { ...prev };
                delete next[name as keyof AnsulCommissioningFormData];
                return next;
            });
        }
    };

    const validateForArchiveSave = (): boolean => {
        const errors: Partial<Record<keyof AnsulCommissioningFormData, string>> = {};

        if (!formData.restaurantName.trim()) errors.restaurantName = t('validation.required');
        if (!formData.technicianName.trim()) errors.technicianName = t('validation.required');
        if (!formData.commissioningDate.trim()) errors.commissioningDate = t('validation.required');

        setFieldErrors(errors);

        const isValid = Object.keys(errors).length === 0;
        if (!isValid) {
            // Ensure at least one invalid field is visible
            if (errors.commissioningDate && !errors.restaurantName && !errors.technicianName) {
                setActiveSection('system');
            } else {
                setActiveSection('restaurant');
            }
            window.scrollTo({ top: 100, behavior: 'smooth' });
        }

        return isValid;
    };

    // Nitrogen cylinder handlers
    const addNitrogenCylinder = () => {
        const newCylinder: NitrogenCylinder = {
            id: generateId(),
            size: 'large',
            stampedDate: '',
            stampedWeight: 0,
            actualWeight: 0
        };
        setFormData(prev => ({
            ...prev,
            nitrogenCylinders: [...prev.nitrogenCylinders, newCylinder]
        }));
    };

    const removeNitrogenCylinder = (id: string) => {
        setFormData(prev => ({
            ...prev,
            nitrogenCylinders: prev.nitrogenCylinders.filter(c => c.id !== id)
        }));
    };

    const updateNitrogenCylinder = (id: string, field: keyof NitrogenCylinder, value: any) => {
        setFormData(prev => ({
            ...prev,
            nitrogenCylinders: prev.nitrogenCylinders.map(c =>
                c.id === id ? { ...c, [field]: value } : c
            )
        }));
    };

    // Manual actuator handlers
    const addManualActuator = () => {
        const newActuator: ManualActuator = {
            id: generateId(),
            location: ''
        };
        setFormData(prev => ({
            ...prev,
            manualActuators: [...prev.manualActuators, newActuator]
        }));
    };

    const removeManualActuator = (id: string) => {
        setFormData(prev => ({
            ...prev,
            manualActuators: prev.manualActuators.filter(a => a.id !== id)
        }));
    };

    const updateManualActuator = (id: string, location: string) => {
        setFormData(prev => ({
            ...prev,
            manualActuators: prev.manualActuators.map(a =>
                a.id === id ? { ...a, location } : a
            )
        }));
    };

    // Detector handlers
    const addDetector = () => {
        const newDetector: AnsulDetector = {
            id: generateId(),
            color: '182-red',
            count: 1,
            location: ''
        };
        setFormData(prev => ({
            ...prev,
            detectors: [...prev.detectors, newDetector]
        }));
    };

    const removeDetector = (id: string) => {
        setFormData(prev => ({
            ...prev,
            detectors: prev.detectors.filter(d => d.id !== id)
        }));
    };

    const updateDetector = (id: string, field: keyof AnsulDetector, value: any) => {
        setFormData(prev => ({
            ...prev,
            detectors: prev.detectors.map(d =>
                d.id === id ? { ...d, [field]: value } : d
            )
        }));
    };

    // Nozzle handlers
    const addNozzle = () => {
        const newNozzle: AnsulNozzle = {
            id: generateId(),
            type: '',
            count: 1,
            location: ''
        };
        setFormData(prev => ({
            ...prev,
            nozzles: [...prev.nozzles, newNozzle]
        }));
    };

    const removeNozzle = (id: string) => {
        setFormData(prev => ({
            ...prev,
            nozzles: prev.nozzles.filter(n => n.id !== id)
        }));
    };

    const updateNozzle = (id: string, field: keyof AnsulNozzle, value: any) => {
        setFormData(prev => ({
            ...prev,
            nozzles: prev.nozzles.map(n =>
                n.id === id ? { ...n, [field]: value } : n
            )
        }));
    };

    // Checklist handlers
    const updateChecklistItem = (id: string, field: 'status' | 'notes', value: string) => {
        setChecklist(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    // Calculate next service date (6 months from commissioning)
    const calculateNextServiceDate = () => {
        if (formData.commissioningDate) {
            const date = new Date(formData.commissioningDate);
            date.setMonth(date.getMonth() + 6);
            setFormData(prev => ({
                ...prev,
                nextServiceDate: date.toISOString().split('T')[0]
            }));
        }
    };

    // Clear form
    const clearForm = () => {
        if (window.confirm(t('common.confirm') + '?')) {
            setFormData(getInitialFormData());
            setChecklist(getInitialChecklist());
            setActiveSection('restaurant');
            setFieldErrors({});
        }
    };

    // Save to archive without preview
    const handleSaveToArchive = () => {
        const isValid = validateForArchiveSave();
        const savingWithWarnings = !isValid;
        if (!isValid) {
            const confirmMessage = t('validation.confirmProceedWithWarnings');
            if (!window.confirm(confirmMessage)) {
                return;
            }
        }

        const report: SavedAnsulCommissioning = {
            id: editReport?.id || generateId(),
            documentNumber: editReport?.documentNumber || generateAnsulCommissioningNumber(),
            reportType: selectedReportType,
            formData,
            checklist,
            settings: {
                printLanguage: settings.printLanguage,
                companyCountry: settings.companyCountry
            },
            createdAt: editReport?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Save to localStorage
        saveAnsulCommissioningItem(report);

        onSave();

        // Clear the form only when saving a fully valid report.
        // If user proceeds with warnings, keep the form so missing fields can be filled.
        if (!savingWithWarnings) {
            setFormData(getInitialFormData());
            setChecklist(getInitialChecklist());
            setActiveSection('restaurant');
            setFieldErrors({});
        }
        
        // Show confirmation
        alert(t('maintenance.savedToArchive') || 'Report saved to archive!');
    };

    // Save and preview
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const report: SavedAnsulCommissioning = {
            id: editReport?.id || generateId(),
            documentNumber: editReport?.documentNumber || generateAnsulCommissioningNumber(),
            reportType: selectedReportType,
            formData,
            checklist,
            settings: {
                printLanguage: settings.printLanguage,
                companyCountry: settings.companyCountry
            },
            createdAt: editReport?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Save to localStorage
        saveAnsulCommissioningItem(report);

        onSave();
        onPreview(report);
    };

    // Progress calculation
    const completedChecklist = checklist.filter(item => item.status !== '').length;
    const progress = Math.round((completedChecklist / checklist.length) * 100);

    return (
        <form onSubmit={handleSubmit} className="ansul-commissioning-form animate-fade-in">
            {/* Technology Type Selector */}
            <div className="section-panel mb-6" data-testid="technology-selector">
                <h2 className="section-panel-heading">
                    <span className="material-symbols-outlined mr-2">category</span>
                    {t('ansulCommissioning.typeSelector.title')}
                </h2>
                <div className="form-group">
                    <label>{t('ansulCommissioning.typeSelector.label')}</label>
                    <select
                        value={selectedReportType}
                        onChange={(e) => setSelectedReportType(e.target.value as CommissioningReportType)}
                        className="form-select"
                        data-testid="technology-type-select"
                    >
                        {COMMISSIONING_TYPES.map(ct => (
                            <option key={ct.value} value={ct.value}>
                                {t(ct.labelKey)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedReportType === 'ansul_r102' ? (
            <div className="maintenance-layout">
                {/* Sidebar */}
                <div className="maintenance-sidebar">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <div className="text-xs font-semibold uppercase text-gray-500 mb-1">{t('maintenance.progress')}</div>
                        <div className="flex items-center space-x-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
                            </div>
                            <span className="text-sm font-bold text-gray-700">{progress}%</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={`maintenance-nav-item ${activeSection === 'restaurant' ? 'active' : ''}`}
                        onClick={() => setActiveSection('restaurant')}
                    >
                        <span className="material-symbols-outlined text-sm mr-2">restaurant</span>
                        <span>{t('ansulCommissioning.restaurant.title')}</span>
                    </button>
                    <button
                        type="button"
                        className={`maintenance-nav-item ${activeSection === 'system' ? 'active' : ''}`}
                        onClick={() => setActiveSection('system')}
                    >
                        <span className="material-symbols-outlined text-sm mr-2">local_fire_department</span>
                        <span>{t('ansulCommissioning.system.title')}</span>
                    </button>
                    <button
                        type="button"
                        className={`maintenance-nav-item ${activeSection === 'components' ? 'active' : ''}`}
                        onClick={() => setActiveSection('components')}
                    >
                        <span className="material-symbols-outlined text-sm mr-2">settings</span>
                        <span>{t('ansulCommissioning.nozzles.title')}</span>
                    </button>
                    <button
                        type="button"
                        className={`maintenance-nav-item ${activeSection === 'checklist' ? 'active' : ''}`}
                        onClick={() => setActiveSection('checklist')}
                    >
                        <span className="material-symbols-outlined text-sm mr-2">checklist</span>
                        <span>{t('ansulCommissioning.checklist.title')}</span>
                        <span className="ml-auto text-xs text-gray-400">{completedChecklist}/{checklist.length}</span>
                    </button>

                    {/* Desktop Action Buttons */}
                    <div className="p-4 border-t border-gray-200 mt-auto hidden lg:flex flex-col gap-3">
                        <button type="submit" className="btn btn-primary w-full justify-center">
                            <span className="material-symbols-outlined text-sm">print</span>
                            {t('ansulCommissioning.actions.print')}
                        </button>
                        <button type="button" onClick={handleSaveToArchive} className="btn btn-success w-full justify-center">
                            <span className="material-symbols-outlined text-sm">save</span>
                            {t('ansulCommissioning.actions.save')}
                        </button>
                        <button type="button" onClick={clearForm} className="btn btn-secondary w-full justify-center">
                            <span className="material-symbols-outlined text-sm">delete_sweep</span>
                            {t('ansulCommissioning.actions.clear')}
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="maintenance-content space-y-6">
                    {/* Mobile Progress Bar */}
                    <div className="lg:hidden bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-200">
                        <div className="flex justify-between text-xs font-semibold uppercase text-gray-500 mb-2">
                            <span>{t('maintenance.progress')}</span>
                            <span>{progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>

                    {/* RESTAURANT SECTION */}
                    <div className={activeSection === 'restaurant' ? 'block' : 'hidden'}>
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">restaurant</span>
                                {t('ansulCommissioning.restaurant.title')}
                            </h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.restaurant.name')}</label>
                                    <input
                                        type="text"
                                        name="restaurantName"
                                        value={formData.restaurantName}
                                        onChange={handleInputChange}
                                        className={`form-input ${fieldErrors.restaurantName ? 'input-error' : ''}`}
                                        required
                                    />
                                    {fieldErrors.restaurantName && (
                                        <span className="error-message">⚠️ {fieldErrors.restaurantName}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.restaurant.address')}</label>
                                    <input
                                        type="text"
                                        name="restaurantAddress"
                                        value={formData.restaurantAddress}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.restaurant.contactPerson')}</label>
                                    <input
                                        type="text"
                                        name="contactPerson"
                                        value={formData.contactPerson}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.restaurant.email')}</label>
                                    <input
                                        type="email"
                                        name="contactEmail"
                                        value={formData.contactEmail}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.restaurant.phone')}</label>
                                    <input
                                        type="tel"
                                        name="contactPhone"
                                        value={formData.contactPhone}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Technician Info */}
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">badge</span>
                                {t('ansulCommissioning.technician.title')}
                            </h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.technician.company')}</label>
                                    <input
                                        type="text"
                                        name="technicianCompany"
                                        value={formData.technicianCompany}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.technician.name')}</label>
                                    <input
                                        type="text"
                                        name="technicianName"
                                        value={formData.technicianName}
                                        onChange={handleInputChange}
                                        className={`form-input ${fieldErrors.technicianName ? 'input-error' : ''}`}
                                        required
                                    />
                                    {fieldErrors.technicianName && (
                                        <span className="error-message">⚠️ {fieldErrors.technicianName}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.technician.phone')}</label>
                                    <input
                                        type="tel"
                                        name="technicianPhone"
                                        value={formData.technicianPhone}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end mt-6">
                            <button type="button" onClick={() => setActiveSection('system')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* SYSTEM SECTION */}
                    <div className={activeSection === 'system' ? 'block' : 'hidden'}>
                        {/* System Info */}
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">local_fire_department</span>
                                {t('ansulCommissioning.system.title')}
                            </h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.system.type')}</label>
                                    <input
                                        type="text"
                                        name="systemType"
                                        value={formData.systemType}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder={t('ansulCommissioning.system.typePlaceholder')}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.system.agent')}</label>
                                    <input
                                        type="text"
                                        name="extinguishingAgent"
                                        value={formData.extinguishingAgent}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder={t('ansulCommissioning.system.agentPlaceholder')}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.system.batchNumber')}</label>
                                    <input
                                        type="text"
                                        name="agentBatchNumber"
                                        value={formData.agentBatchNumber}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Schedule */}
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">calendar_month</span>
                                {t('ansulCommissioning.schedule.title')}
                            </h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.schedule.serviceCycle')}</label>
                                    <select
                                        name="serviceCycle"
                                        value={formData.serviceCycle}
                                        onChange={handleInputChange}
                                        className="form-select"
                                    >
                                        <option value="6kk">6 kk</option>
                                        <option value="12kk">12 kk</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.schedule.commissioningDate')}</label>
                                    <input
                                        type="date"
                                        name="commissioningDate"
                                        value={formData.commissioningDate}
                                        onChange={(e) => {
                                            handleInputChange(e);
                                            // Auto-calculate next service date
                                            setTimeout(calculateNextServiceDate, 100);
                                        }}
                                        className={`form-input ${fieldErrors.commissioningDate ? 'input-error' : ''}`}
                                        required
                                    />
                                    {fieldErrors.commissioningDate && (
                                        <span className="error-message">⚠️ {fieldErrors.commissioningDate}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.schedule.nextServiceDate')}</label>
                                    <input
                                        type="date"
                                        name="nextServiceDate"
                                        value={formData.nextServiceDate}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tanks */}
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">propane_tank</span>
                                {t('ansulCommissioning.tanks.title')}
                            </h2>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.tanks.volume')}</label>
                                    <input
                                        type="text"
                                        name="tankVolume"
                                        value={formData.tankVolume}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder={t('ansulCommissioning.tanks.volumePlaceholder')}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.tanks.filledDate')}</label>
                                    <input
                                        type="date"
                                        name="tankFilledDate"
                                        value={formData.tankFilledDate}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('ansulCommissioning.tanks.fluidExpiry')}</label>
                                    <input
                                        type="text"
                                        name="fluidExpiryDate"
                                        value={formData.fluidExpiryDate}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="esim. 6/2035"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Nitrogen Cylinders */}
                        <div className="section-panel">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="section-panel-heading">
                                    <span className="material-symbols-outlined mr-2">gas_meter</span>
                                    {t('ansulCommissioning.nitrogen.title')}
                                </h2>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={addNitrogenCylinder}
                                >
                                    {t('ansulCommissioning.nitrogen.addCylinder')}
                                </button>
                            </div>

                            {formData.nitrogenCylinders.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">
                                    {t('ansulCommissioning.nitrogen.noCylinders')}
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {formData.nitrogenCylinders.map((cylinder, index) => (
                                        <div key={cylinder.id} className="item-card">
                                            <div className="flex justify-between items-start mb-3">
                                                <span className="font-medium text-gray-700">#{index + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeNitrogenCylinder(cylinder.id)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <span className="material-symbols-outlined text-sm">close</span>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.nitrogen.size')}</label>
                                                    <select
                                                        value={cylinder.size}
                                                        onChange={(e) => updateNitrogenCylinder(cylinder.id, 'size', e.target.value)}
                                                        className="form-select"
                                                    >
                                                        <option value="small">{t('ansulCommissioning.nitrogen.sizeSmall')}</option>
                                                        <option value="large">{t('ansulCommissioning.nitrogen.sizeLarge')}</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.nitrogen.stampedDate')}</label>
                                                    <input
                                                        type="text"
                                                        value={cylinder.stampedDate}
                                                        onChange={(e) => updateNitrogenCylinder(cylinder.id, 'stampedDate', e.target.value)}
                                                        className="form-input"
                                                        placeholder="esim. 09/2024"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.nitrogen.stampedWeight')}</label>
                                                    <input
                                                        type="number"
                                                        value={cylinder.stampedWeight || ''}
                                                        onChange={(e) => updateNitrogenCylinder(cylinder.id, 'stampedWeight', Number(e.target.value))}
                                                        className="form-input"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.nitrogen.actualWeight')}</label>
                                                    <input
                                                        type="number"
                                                        value={cylinder.actualWeight || ''}
                                                        onChange={(e) => updateNitrogenCylinder(cylinder.id, 'actualWeight', Number(e.target.value))}
                                                        className="form-input"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4">
                                <label className="form-label">{t('ansulCommissioning.nitrogen.renewalDate')}</label>
                                <input
                                    type="text"
                                    name="cylinderRenewalDate"
                                    value={formData.cylinderRenewalDate}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder="esim. 09/34"
                                />
                            </div>
                        </div>

                        {/* Gas Shutoff */}
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">emergency</span>
                                {t('ansulCommissioning.gasShutoff.title')}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="form-label">{t('ansulCommissioning.gasShutoff.type')}</label>
                                    <select
                                        name="gasShutoffType"
                                        value={formData.gasShutoffType}
                                        onChange={handleInputChange}
                                        className="form-select"
                                    >
                                        <option value="mechanical">{t('ansulCommissioning.gasShutoff.mechanical')}</option>
                                        <option value="electrical">{t('ansulCommissioning.gasShutoff.electrical')}</option>
                                        <option value="none">{t('ansulCommissioning.gasShutoff.none')}</option>
                                        <option value="automation">{t('ansulCommissioning.gasShutoff.automation')}</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="form-label">{t('ansulCommissioning.gasShutoff.notes')}</label>
                                    <textarea
                                        name="gasShutoffNotes"
                                        value={formData.gasShutoffNotes}
                                        onChange={handleInputChange}
                                        className="form-textarea"
                                        rows={2}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between mt-6">
                            <button type="button" onClick={() => setActiveSection('restaurant')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                            <button type="button" onClick={() => setActiveSection('components')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* COMPONENTS SECTION */}
                    <div className={activeSection === 'components' ? 'block' : 'hidden'}>
                        {/* Manual Actuators */}
                        <div className="section-panel">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="section-panel-heading">
                                    <span className="material-symbols-outlined mr-2">pan_tool</span>
                                    {t('ansulCommissioning.actuators.title')}
                                </h2>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={addManualActuator}
                                >
                                    {t('ansulCommissioning.actuators.addActuator')}
                                </button>
                            </div>

                            {formData.manualActuators.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">
                                    Ei manuaalilaukaisimia lisätty
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {formData.manualActuators.map((actuator, index) => (
                                        <div key={actuator.id} className="flex gap-3 items-center">
                                            <span className="text-muted" style={{ width: "2rem" }}>{index + 1}.</span>
                                            <input
                                                type="text"
                                                value={actuator.location}
                                                onChange={(e) => updateManualActuator(actuator.id, e.target.value)}
                                                className="form-input flex-1"
                                                placeholder={t('ansulCommissioning.actuators.locationPlaceholder')}
                                            />
                                            <button
                                                type="button"
                                                className="btn-icon btn-danger"
                                                onClick={() => removeManualActuator(actuator.id)}
                                            >
                                                <span className="material-symbols-outlined">delete</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Detectors */}
                        <div className="section-panel">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="section-panel-heading">
                                    <span className="material-symbols-outlined mr-2">sensors</span>
                                    {t('ansulCommissioning.detectors.title')}
                                </h2>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={addDetector}
                                >
                                    {t('ansulCommissioning.detectors.addDetector')}
                                </button>
                            </div>

                            {formData.detectors.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">
                                    {t('ansulCommissioning.detectors.noDetectors')}
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {formData.detectors.map((detector, index) => (
                                        <div key={detector.id} className="item-card">
                                            <div className="flex justify-between items-start mb-3">
                                                <span className="font-medium text-gray-700">#{index + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeDetector(detector.id)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <span className="material-symbols-outlined text-sm">close</span>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.detectors.color')}</label>
                                                    <select
                                                        value={detector.color}
                                                        onChange={(e) => updateDetector(detector.id, 'color', e.target.value)}
                                                        className="form-select"
                                                    >
                                                        {DETECTOR_COLORS.map(color => (
                                                            <option key={color} value={color}>
                                                                {t(`ansulCommissioning.detectors.colors.${color}`)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.detectors.count')}</label>
                                                    <input
                                                        type="number"
                                                        value={detector.count}
                                                        onChange={(e) => updateDetector(detector.id, 'count', Number(e.target.value))}
                                                        className="form-input"
                                                        min={1}
                                                    />
                                                </div>
                                                <div className="lg:col-span-2">
                                                    <label className="form-label">{t('ansulCommissioning.detectors.location')}</label>
                                                    <input
                                                        type="text"
                                                        value={detector.location}
                                                        onChange={(e) => updateDetector(detector.id, 'location', e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4">
                                <label className="form-label">{t('ansulCommissioning.detectors.notes')}</label>
                                <textarea
                                    name="detectorNotes"
                                    value={formData.detectorNotes}
                                    onChange={handleInputChange}
                                    className="form-textarea"
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Nozzles */}
                        <div className="section-panel">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="section-panel-heading">
                                    <span className="material-symbols-outlined mr-2">water_drop</span>
                                    {t('ansulCommissioning.nozzles.title')}
                                </h2>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={addNozzle}
                                >
                                    {t('ansulCommissioning.nozzles.addNozzle')}
                                </button>
                            </div>

                            {formData.nozzles.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">
                                    {t('ansulCommissioning.nozzles.noNozzles')}
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {formData.nozzles.map((nozzle, index) => (
                                        <div key={nozzle.id} className="item-card">
                                            <div className="flex justify-between items-start mb-3">
                                                <span className="font-medium text-gray-700">#{index + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeNozzle(nozzle.id)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <span className="material-symbols-outlined text-sm">close</span>
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.nozzles.type')}</label>
                                                    <input
                                                        type="text"
                                                        value={nozzle.type}
                                                        onChange={(e) => updateNozzle(nozzle.id, 'type', e.target.value)}
                                                        className="form-input"
                                                        placeholder={t('ansulCommissioning.nozzles.typePlaceholder')}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="form-label">{t('ansulCommissioning.nozzles.count')}</label>
                                                    <input
                                                        type="number"
                                                        value={nozzle.count}
                                                        onChange={(e) => updateNozzle(nozzle.id, 'count', Number(e.target.value))}
                                                        className="form-input"
                                                        min={1}
                                                    />
                                                </div>
                                                <div className="lg:col-span-2">
                                                    <label className="form-label">{t('ansulCommissioning.nozzles.location')}</label>
                                                    <input
                                                        type="text"
                                                        value={nozzle.location}
                                                        onChange={(e) => updateNozzle(nozzle.id, 'location', e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4">
                                <label className="form-label">{t('ansulCommissioning.nozzles.notes')}</label>
                                <textarea
                                    name="nozzleNotes"
                                    value={formData.nozzleNotes}
                                    onChange={handleInputChange}
                                    className="form-textarea"
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Additional Notes */}
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">notes</span>
                                {t('ansulCommissioning.additionalNotes')}
                            </h2>
                            <textarea
                                name="additionalNotes"
                                value={formData.additionalNotes}
                                onChange={handleInputChange}
                                className="form-textarea"
                                rows={4}
                            />
                        </div>

                        <div className="flex justify-between mt-6">
                            <button type="button" onClick={() => setActiveSection('system')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                            <button type="button" onClick={() => setActiveSection('checklist')} className="btn btn-primary">
                                {t('maintenance.actions.next')}
                                <span className="material-symbols-outlined text-sm">arrow_forward</span>
                            </button>
                        </div>
                    </div>

                    {/* CHECKLIST SECTION */}
                    <div className={activeSection === 'checklist' ? 'block' : 'hidden'}>
                        <div className="section-panel">
                            <h2 className="section-panel-heading">
                                <span className="material-symbols-outlined mr-2">checklist</span>
                                {t('ansulCommissioning.checklist.title')}
                            </h2>

                            <div className="checklist-table">
                                <div className="checklist-header">
                                    <div className="checklist-col-item">Tarkistuskohde</div>
                                    <div className="checklist-col-status">{t('ansulCommissioning.checklist.status')}</div>
                                    <div className="checklist-col-notes">{t('ansulCommissioning.checklist.notes')}</div>
                                </div>

                                {checklist.map((item, index) => (
                                    <div key={item.id} className="checklist-row">
                                        <div className="checklist-col-item">
                                            <span className="checklist-number">{index + 1}.</span>
                                            {t(`ansulCommissioning.checklist.items.${item.labelKey}`)}
                                        </div>
                                        <div className="checklist-col-status">
                                            <div className="status-buttons">
                                                <button
                                                    type="button"
                                                    className={`status-btn status-ok ${item.status === 'ok' ? 'active' : ''}`}
                                                    onClick={() => updateChecklistItem(item.id, 'status', 'ok')}
                                                >
                                                    {t('ansulCommissioning.checklist.ok')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`status-btn status-nok ${item.status === 'nok' ? 'active' : ''}`}
                                                    onClick={() => updateChecklistItem(item.id, 'status', 'nok')}
                                                >
                                                    {t('ansulCommissioning.checklist.nok')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`status-btn status-na ${item.status === 'na' ? 'active' : ''}`}
                                                    onClick={() => updateChecklistItem(item.id, 'status', 'na')}
                                                >
                                                    {t('ansulCommissioning.checklist.na')}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="checklist-col-notes">
                                            <input
                                                type="text"
                                                value={item.notes}
                                                onChange={(e) => updateChecklistItem(item.id, 'notes', e.target.value)}
                                                className="form-input"
                                                placeholder={t('ansulCommissioning.checklist.notes')}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mobile Action Buttons */}
                        <div className="flex justify-between mt-6 lg:hidden">
                            <button type="button" onClick={() => setActiveSection('components')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                            <button type="submit" className="btn btn-primary">
                                <span className="material-symbols-outlined text-sm">print</span>
                                {t('ansulCommissioning.actions.print')}
                            </button>
                        </div>

                        {/* Desktop - just back button since print is in sidebar */}
                        <div className="hidden lg:flex justify-start mt-6">
                            <button type="button" onClick={() => setActiveSection('components')} className="btn btn-secondary">
                                <span className="material-symbols-outlined text-sm">arrow_back</span>
                                {t('maintenance.actions.back')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            ) : (
            /* Coming Soon placeholder for non-Ansul report types */
            <div className="section-panel" data-testid="coming-soon-placeholder">
                <div className="text-center py-16">
                    <span className="material-symbols-outlined" style={{ fontSize: '64px', color: 'var(--halton-deep-blue)', opacity: 0.3 }}>
                        {COMMISSIONING_TYPES.find(ct => ct.value === selectedReportType)?.icon || 'construction'}
                    </span>
                    <h3 className="text-xl font-semibold mt-4" style={{ color: 'var(--halton-deep-blue)' }}>
                        {t(COMMISSIONING_TYPES.find(ct => ct.value === selectedReportType)?.labelKey || '')}
                    </h3>
                    <p className="text-gray-500 mt-2">
                        {t('ansulCommissioning.typeSelector.comingSoon')}
                    </p>
                </div>
            </div>
            )}
        </form>
    );
};

export default AnsulCommissioningForm;
